import { useCallback, useState } from "react";
import { processModelCreator } from "../models/process.model";
import {
  useAddStudioProcessToBranchMutation,
  useCreateAppProcessMutation,
  useUpdateMegaJSONMutation
} from "@certa/queries/hooks/studio.hook";
import { getSlugifiedTag } from "@certa/studio-core";
import type { ZodRawProcess } from "@certa/queries/schemas/studio/deployableJSON.schema";
import { useUpdateAppJSONMutation } from "@certa/queries/queries/appJSON/updateAppJSON";
import { useIsECAUIEnabled } from "@certa/common/hooks/useCanViewStudio2";
import { forwardParseAppMetaJSONSync } from "@certa/studio-parser";

/**
 * Imports a process JSON to an app
 */
export const useImportProcessJSON = ({
  packageId,
  branchId,
  savepointId,
  onSuccess
}: {
  packageId: number;
  branchId: number;
  savepointId: number;
  onSuccess?: (processId: number) => void;
}) => {
  const {
    mutateAsync: createAppProcess,
    isLoading: isCreateAppProcessLoading
  } = useCreateAppProcessMutation();
  const {
    mutateAsync: addStudioProcessToBranch,
    isLoading: isAddStudioProcessToBranchLoading
  } = useAddStudioProcessToBranchMutation();
  const { mutateAsync: updateMegaJSON, isLoading: isUpdateMegaJSONLoading } =
    useUpdateMegaJSONMutation();
  const { mutateAsync: updateAppJSON, isLoading: isUpdateAppJSONLoading } =
    useUpdateAppJSONMutation();
  const isECAEnabled = useIsECAUIEnabled();

  const isLoading =
    isCreateAppProcessLoading ||
    isAddStudioProcessToBranchLoading ||
    isUpdateAppJSONLoading ||
    isUpdateMegaJSONLoading;

  const [error, setError] = useState<string | null>(null);

  const importJSON = useCallback(
    async (json: ZodRawProcess) => {
      if (!json) return;
      setError(null);

      try {
        // Create a process
        const responseData = await createAppProcess({
          name: json.name,
          description: undefined,
          workflow_kind_tag: json.kind.tag || getSlugifiedTag(json.name, "-"),
          package_id: packageId
        });

        await addStudioProcessToBranch({
          process_id: responseData.id,
          branch_id: branchId
        });

        // Reverse parse json
        const metaJSON = processModelCreator(json, {
          processId: responseData.id.toString()
        });

        if (isECAEnabled) {
          await updateAppJSON({
            branchSavepointId: savepointId,
            payload: [
              {
                op: "create",
                path: `processes.${responseData.id}`,
                value: forwardParseAppMetaJSONSync({ metaJSON })
              }
            ]
          });
        } else {
          // Initialise metaJSON to the process
          await updateMegaJSON({
            branchSavepointId: savepointId,
            payload: {
              create_or_update: [
                {
                  path: `processes.${responseData.id}`,
                  data: metaJSON
                }
              ]
            }
          });
        }

        onSuccess?.(responseData.id);
        setError(null);
      } catch (err) {
        setError(
          "Something went wrong while importing the process. Please make sure that a process with the same name does not already exist in the system."
        );
      }
    },
    [
      addStudioProcessToBranch,
      branchId,
      createAppProcess,
      onSuccess,
      packageId,
      savepointId,
      updateMegaJSON,
      isECAEnabled,
      updateAppJSON
    ]
  );

  return { importJSON, isLoading, error };
};
